import React from 'react';
import { ErrorMessage, FlexRow, Grid, Header3Text } from 'ui-toolkit';
import { Field, FieldArray, FieldProps } from 'formik';
import { CreateEditAchPushRow } from './CreateEditAchPushRow';
import { IAchPushDisbursementData } from 'types/order/escrow/IAchPushDisbursementData';
import { RootStore } from 'store/types';
import { useKeyToSelect } from 'store/createKeyedDataAsyncSlice';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { UUID } from 'types/shared/UUID';
import { useEnvironmentVariables } from 'ui-toolkit/environmentVariables/useEnvironmentVariables';
import { DeleteAchPushSwitchWindow } from './createWireFromACH/DeleteAchPushSwitchWindow';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { IPartyPerson } from 'ui-toolkit/orders/interfaces/IPartyPerson';
import { BlockRedirection } from 'components/cdBuilder/BlockRedirection';
import { useHasPermissions } from 'components/accessControl/hooks/useHasPermissions';
import { CheckForAvailableFundsModal } from '../../escrowCreateEdit/checkForAvailableFundsModal/CheckForAvailableFundsModal';
import { EscrowRemittanceACHContainer } from '../../escrowRemittanceACH/EscrowRemittanceACHContainer';
import { useCreateEditAchPush } from '../../hooks/useCreateEditAchPush';
import { HasPermissionButton } from '../../shared/HasPermissionButton';

export interface ICreateEditAchPushesContainerProps {
  orderId: UUID;
  dirty: boolean;
  escrowFileId: string;
  parties: IPartyPerson[];
  onAcknowledgeSuccess: () => void;
  onTransactionUpdate: () => void;
  availableAmountDollars: number;
}

export const CreateEditAchPushesContainer: React.FC<ICreateEditAchPushesContainerProps> = ({
  orderId,
  dirty,
  escrowFileId,
  parties,
  onAcknowledgeSuccess,
  onTransactionUpdate,
  availableAmountDollars,
}) => {
  const { data } = useEnvironmentVariables();
  const hasEditPermission = useHasPermissions([ePermissions.EscrowExpectedAchPushDepositsEdit]);
  const { data: order } = useSelectCurrentOrder();

  const {
    showWarningModal,
    setShowWarningModal,
    showDeletingACHPush,
    setShowDeletingACHPush,
    sendAchId,
    fetchStatus,
    fetchError,
    onRemoveAchPush,
    onSendAchDisbursement,
  } = useCreateEditAchPush({
    escrowFileId,
    availableAmountDollars,
    onTransactionUpdate,
  });

  const { data: achAccounts } = useKeyToSelect(
    escrowFileId,
    (store: RootStore) => store.order.escrow.getAchAccountsExcludingAD
  );

  return (
    <>
      <BlockRedirection showSaveSpinner={false} showSaveSuccess={false} isDirty={dirty} />
      <FieldArray name={'achPushDisbursements'}>
        {({ push, remove }) => (
          <>
            <Grid>
              <FlexRow>
                <Header3Text>Payment Manager Automated ACH Pushes</Header3Text>
                <HasPermissionButton
                  hasEditPermission={hasEditPermission}
                  push={push}
                  successMessage={'Add Payment Manager Automated ACH Push'}
                  transferType={'automated ACH pushes'}
                />
              </FlexRow>
              <ErrorMessage error={fetchError} />
              <EscrowRemittanceACHContainer
                orderId={orderId}
                escrowFileId={escrowFileId}
                americanDigitalAchId={data?.AmericanDigitalAchId!}
              />
              <Field name={'achPushDisbursements'}>
                {({ field }: FieldProps<IAchPushDisbursementData[]>) => (
                  <>
                    {field.value?.map((achPush, index) => (
                      <>
                        <CreateEditAchPushRow
                          achPush={achPush}
                          key={index}
                          index={index}
                          onDelete={async (index) => {
                            remove(index);
                            await onRemoveAchPush(achPush);
                          }}
                          fieldName={'achPushDisbursements'}
                          achAccounts={achAccounts || []}
                          parties={parties}
                          onSend={onSendAchDisbursement}
                          onAcknowledgeSuccess={onAcknowledgeSuccess}
                          escrowFileId={escrowFileId}
                          state={order?.property.state || ''}
                          fetching={fetchStatus.fetching && sendAchId === achPush.id}
                          setShowDeletingACHPush={setShowDeletingACHPush}
                        />
                        {showWarningModal && (
                          <CheckForAvailableFundsModal
                            submitData={() => onSendAchDisbursement(achPush)}
                            type="ach"
                            handleClose={() => setShowWarningModal(false)}
                          />
                        )}
                      </>
                    ))}
                  </>
                )}
              </Field>
            </Grid>
          </>
        )}
      </FieldArray>
      {showDeletingACHPush && <DeleteAchPushSwitchWindow onAcknowledgeSuccess={() => setShowDeletingACHPush(false)} />}
    </>
  );
};
